import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import useCalculatorQuote from "@/hooks/useCalculatorQuote";
import { toast } from "sonner";
import {
    Info,
    CheckCircle2,
    AlertTriangle,
    Sparkles,
    Loader2,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Perx branded insurance premium calculator.
//
// Every number on this page comes from the server (see
// backend/routes/calculator.py): coverage choices go up, priced sections and
// discounted totals come back. Nothing is computed here, and no premium is
// ever sent — Apply recomputes server-side from the choices alone.
//
// The plan / deductible / cap / area / network strings are likewise served,
// not hardcoded: each one is a literal substring of the rate-table lookup key,
// so a copy edit in this file would silently break pricing.
// ---------------------------------------------------------------------------

// Stable per-section presentation. The option values themselves come from
// /member/calculator/options; only the testids and layout live here.
const SECTION_UI = {
    surgery: { testid: "cov-hs" },
    outpatient: { testid: "cov-op" },
    maternity: { testid: "cov-maternity" },
    dental: { testid: "cov-dental" },
};

const FREQUENCIES = [
    { label: "Quarterly", key: "quarterly" },
    { label: "Semi-Annual", key: "semi_annual" },
    { label: "Annual", key: "annual" },
];

function computeAge(dobIso) {
    if (!dobIso) return "";
    const dob = new Date(dobIso);
    if (Number.isNaN(dob.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age;
}

const money = (n) =>
    Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/** Blank coverage state built from the server's option defaults. */
function coverageDefaults(options) {
    if (!options) return null;
    const sections = {};
    Object.entries(options.sections).forEach(([key, cfg]) => {
        sections[key] = { ...(cfg.defaults || { enabled: false }) };
    });
    const residences = options.residences || [];
    const preferred =
        residences.find((z) => z.residence === "Hong Kong") || residences[0];
    return {
        area: options.defaults.area,
        network: options.defaults.network,
        residence: preferred ? preferred.residence : "",
        sections,
    };
}

function buildPrimaryMember(user, defaults) {
    const profile = user?.profile || {};
    return {
        id: "primary",
        isPrimary: true,
        name: user?.name || "Primary member",
        gender: profile.gender || "",
        dob: profile.date_of_birth || "",
        ...defaults,
    };
}

function buildMemberFromSub(sub, defaults) {
    return {
        id: sub.id,
        isPrimary: false,
        name: sub.name,
        gender: sub.gender || "",
        dob: sub.dob || "",
        ...defaults,
    };
}

export default function Calculator() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dealIdParam = searchParams.get("deal");
    const { user } = useAuth();

    const [subs, setSubs] = useState([]);
    const [options, setOptions] = useState(null);
    const [optionsError, setOptionsError] = useState(null);
    const [members, setMembers] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const [frequency, setFrequency] = useState("Annual");
    const [busy, setBusy] = useState(false);
    const [isPaidUSD, setIsPaidUSD] = useState(false);

    const defaults = useMemo(() => coverageDefaults(options), [options]);

    useEffect(() => {
        api.get("/member/calculator/options")
            .then((r) => setOptions(r.data))
            .catch((e) => setOptionsError(formatApiError(e)));
    }, []);

    useEffect(() => {
        api.get("/member/sub-users")
            .then((r) => setSubs(r.data || []))
            .catch(() => setSubs([]));
    }, []);

    useEffect(() => {
        if (!dealIdParam) return;
        api.get(`/public/deals/${dealIdParam}`)
            .then((r) => setIsPaidUSD(Boolean(r.data?.is_paid_USD)))
            .catch(() => setIsPaidUSD(false));
    }, [dealIdParam]);

    // Seed (and keep in sync) the primary row once both the profile and the
    // server's option defaults are available. Coverage choices already made
    // are preserved — only the identity fields refresh.
    useEffect(() => {
        if (!defaults) return;
        setMembers((ms) => {
            const primary = buildPrimaryMember(user, defaults);
            if (!ms.length) return [primary];
            const [existing, ...rest] = ms;
            return [
                {
                    ...primary,
                    area: existing.area,
                    network: existing.network,
                    residence: existing.residence,
                    sections: existing.sections,
                },
                ...rest,
            ];
        });
    }, [user, defaults]);

    // Eligibility: an adult sub-user counts only while they hold an active
    // membership seat; a minor counts only while the adult who provides for
    // them is currently included in the calculator.
    const eligibleAdults = useMemo(
        () => subs.filter((s) => !s.is_dependant && s.membershipInForce === true),
        [subs]
    );
    const eligibleAdultIds = useMemo(
        () => new Set(eligibleAdults.map((s) => s.id)),
        [eligibleAdults]
    );
    const dependantsByProvider = useMemo(() => {
        const map = new Map();
        subs
            .filter(
                (s) =>
                    s.is_dependant &&
                    s.primary_provider_id &&
                    eligibleAdultIds.has(s.primary_provider_id)
            )
            .forEach((d) => {
                const list = map.get(d.primary_provider_id) || [];
                list.push(d);
                map.set(d.primary_provider_id, list);
            });
        return map;
    }, [subs, eligibleAdultIds]);
    const includedSubIds = useMemo(
        () => new Set(members.filter((m) => !m.isPrimary).map((m) => m.id)),
        [members]
    );

    const currencyLabel = isPaidUSD ? "USD" : "HKD";
    const symbol = isPaidUSD ? "US$" : "$";

    // ---- Quote -----------------------------------------------------------
    const quotePayload = useMemo(
        () =>
            members.map((m) => ({
                key: m.id,
                name: m.name,
                dob: m.dob,
                gender: m.gender,
                area: m.area,
                network: m.network,
                residence: m.residence,
                sections: m.sections,
            })),
        [members]
    );
    const { quote, loading: quoting, error: quoteError } = useCalculatorQuote(quotePayload);

    const quoteByKey = useMemo(() => {
        const map = new Map();
        (quote?.members || []).forEach((m) => map.set(m.key, m));
        return map;
    }, [quote]);

    const totals = quote?.totals;
    const eligibleCount = quote?.eligible_member_keys?.length || 0;

    const active = members[activeIdx];
    const activeQuote = active ? quoteByKey.get(active.id) : null;

    const setActive = useCallback(
        (patch) =>
            setMembers((ms) =>
                ms.map((m, i) => (i === activeIdx ? { ...m, ...patch } : m))
            ),
        [activeIdx]
    );

    const setSection = useCallback(
        (section, patch) =>
            setMembers((ms) =>
                ms.map((m, i) =>
                    i === activeIdx
                        ? {
                              ...m,
                              sections: {
                                  ...m.sections,
                                  [section]: { ...m.sections[section], ...patch },
                              },
                          }
                        : m
                )
            ),
        [activeIdx]
    );

    // Checking a sub-user adds them to the calculation; unchecking removes
    // them. Unchecking an adult also drops any of their dependants currently
    // included, since a dependant can only be included while their provider is.
    const toggleSub = (sub) => {
        const isIncluded = includedSubIds.has(sub.id);
        setMembers((ms) => {
            if (isIncluded) {
                const cascadeIds = new Set([
                    sub.id,
                    ...(dependantsByProvider.get(sub.id) || []).map((d) => d.id),
                ]);
                return ms.filter((m) => !cascadeIds.has(m.id));
            }
            return [...ms, buildMemberFromSub(sub, defaults)];
        });
        setActiveIdx(isIncluded ? 0 : members.length);
    };

    const apply = async () => {
        setBusy(true);
        try {
            const r = await api.post("/member/calculator/apply", {
                members: quotePayload,
                deal_id: dealIdParam || null,
                frequency,
                sub_user_ids: Array.from(includedSubIds),
            });
            const ref = r.data?.display_id;
            if (dealIdParam) {
                toast.success(
                    `Application ${ref} submitted — the vendor will follow up with a firm quote.`
                );
                // `resume=calculator` tells the deal page to reopen the
                // purchase workflow past the calculator step (e.g. straight
                // to booking) instead of dropping the member back at a
                // fresh, unstarted page — see WorkflowRunner's `initialStep`.
                navigate(`/member/deals/${dealIdParam}?resume=calculator`);
            } else {
                toast.success(
                    `Application ${ref} submitted · ${currencyLabel} ${money(
                        r.data?.totals?.perx?.annual
                    )} a year`
                );
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    if (optionsError) {
        return (
            <>
                <PageHeader
                    eyebrow="Insurance"
                    title="Coverage selection"
                    testid="calculator-header"
                />
                <div className="px-6 lg:px-12 py-8">
                    <div className="rounded-2xl bg-white border border-red-200 p-6 text-sm text-navy-700">
                        <div className="font-semibold text-red-600 mb-1">
                            The calculator couldn&rsquo;t load.
                        </div>
                        {optionsError}
                    </div>
                </div>
            </>
        );
    }

    if (!options || !active) {
        return (
            <>
                <PageHeader
                    eyebrow="Insurance"
                    title="Coverage selection"
                    testid="calculator-header"
                />
                <div className="px-6 lg:px-12 py-16 flex justify-center">
                    <Loader2 className="animate-spin text-navy-300" size={28} />
                </div>
            </>
        );
    }

    const freqValue = (key) => totals?.perx?.[key] ?? 0;

    return (
        <>
            <PageHeader
                eyebrow="Insurance"
                title="Coverage selection"
                description="Estimate your Perx-discounted premium across your household. Choose which eligible family members to include below."
                action={
                    <Button
                        onClick={apply}
                        disabled={busy || quoting || eligibleCount === 0}
                        data-testid="calc-apply"
                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-8 font-semibold tracking-widest disabled:opacity-50"
                    >
                        {busy ? "SENDING…" : "APPLY"}
                    </Button>
                }
                testid="calculator-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                {quoteError && (
                    <div className="rounded-2xl bg-white border border-red-200 p-4 text-sm text-red-600">
                        Couldn&rsquo;t reach the pricing service. {formatApiError(quoteError)}
                    </div>
                )}

                {/* ---------- Top summary strip ---------- */}
                <div className="grid lg:grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-white shadow-soft p-5 border border-navy-100/60">
                        <SummaryRow
                            label="Total Market Rate"
                            value={totals?.market?.annual}
                            symbol={symbol}
                        />
                        <SummaryRow
                            label="Perx Discounted Premium"
                            value={totals?.discount_adjusted_annual}
                            tone="text-navy-700"
                            symbol={symbol}
                        />
                        <SummaryRow
                            label="Additional welcome discount"
                            value={totals?.welcome_discount_annual}
                            tone="text-navy-500"
                            negative
                            symbol={symbol}
                        />
                    </div>
                    <div className="rounded-2xl bg-gradient-to-br from-navy-700 to-navy-800 text-white shadow-soft p-6 relative">
                        {quoting && (
                            <Loader2
                                className="animate-spin absolute top-4 right-4 text-gold-500"
                                size={16}
                            />
                        )}
                        <div className="grid grid-cols-2 gap-6 items-center">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500">
                                    Total Perx discounted premium
                                </div>
                                <div
                                    className="font-display text-4xl mt-2 leading-none"
                                    data-testid="calc-total-perx"
                                >
                                    {currencyLabel} {money(totals?.perx?.annual)}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500">
                                    You save with us
                                </div>
                                <div
                                    className="font-display text-3xl mt-2 leading-none text-emerald-300"
                                    data-testid="calc-savings"
                                >
                                    {currencyLabel} {money(totals?.savings_annual)}
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-2">
                            {FREQUENCIES.map((f) => {
                                const selected = frequency === f.label;
                                return (
                                    <button
                                        key={f.label}
                                        type="button"
                                        data-testid={`calc-freq-${f.label.toLowerCase()}`}
                                        onClick={() => setFrequency(f.label)}
                                        className={`rounded-lg p-3 text-left transition-colors ${
                                            selected
                                                ? "bg-gold-500 text-navy-900"
                                                : "bg-white/10 hover:bg-white/20 text-navy-50"
                                        }`}
                                    >
                                        <div className="text-[10px] uppercase tracking-widest opacity-80">
                                            {f.label}
                                        </div>
                                        <div className="font-display text-lg mt-1">
                                            {symbol}
                                            {money(freqValue(f.key))}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="text-[10px] text-navy-200/70 mt-2">
                            Instalments carry a 4% loading semi-annually and 5% quarterly.
                        </div>
                    </div>
                </div>

                {/* ---------- Body — sidebar + form ---------- */}
                <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
                    {/* Left sidebar — enrolled members */}
                    <aside className="space-y-4">
                        <div className="rounded-2xl bg-cream border border-gold-200/60 p-4 flex gap-3">
                            <Info className="text-gold-600 shrink-0 mt-0.5" size={16} />
                            <p className="text-[11px] text-navy-700 leading-relaxed">
                                Adults with an active membership can be included below.
                                A child under 18 can only be included once the parent who
                                provides for them is included too.
                            </p>
                        </div>

                        {members.map((m, i) => {
                            const selected = activeIdx === i;
                            const q = quoteByKey.get(m.id);
                            const ready = Boolean(q?.eligible);
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setActiveIdx(i)}
                                    data-testid={`calc-member-tab-${i}`}
                                    className={`w-full text-left rounded-2xl p-4 border transition-all ${
                                        selected
                                            ? "bg-white border-gold-500 shadow-soft"
                                            : "bg-white/70 border-navy-100 hover:border-navy-300"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="font-display text-base text-navy-700 leading-tight">
                                                {m.name || `Member ${i + 1}`}
                                                {m.isPrimary && (
                                                    <span className="text-[10px] uppercase tracking-widest text-gold-600 ml-2 align-middle">
                                                        You
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-navy-500 mt-0.5">
                                                Age: {q?.age ?? computeAge(m.dob) ?? "—"} ·{" "}
                                                {(m.gender || "—").toLowerCase()}
                                            </div>
                                        </div>
                                        {ready ? (
                                            <CheckCircle2
                                                className="text-emerald-500 shrink-0"
                                                size={16}
                                                data-testid={`calc-member-ready-${i}`}
                                            />
                                        ) : (
                                            <AlertTriangle
                                                className="text-amber-500 shrink-0"
                                                size={16}
                                                data-testid={`calc-member-blocked-${i}`}
                                            />
                                        )}
                                    </div>
                                    <div className="text-[11px] text-navy-500 mt-3 space-y-0.5">
                                        <div>
                                            Total Market Rate:{" "}
                                            <span className="text-navy-700">
                                                {symbol}
                                                {money(q?.market_rate)}
                                            </span>
                                        </div>
                                        <div>
                                            Total Perx Premium:{" "}
                                            <span className="text-navy-700">
                                                {symbol}
                                                {money(q?.discount_adjusted_premium)}
                                            </span>
                                        </div>
                                        <div>
                                            Total Perx Adjusted Premium:{" "}
                                            <span className="text-emerald-700 font-semibold">
                                                {symbol}
                                                {money(q?.perx_adjusted_premium)}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}

                        {eligibleAdults.length > 0 && (
                            <div className="rounded-2xl bg-white border border-navy-100 p-4 space-y-2.5">
                                <div className="text-[10px] uppercase tracking-widest text-navy-500 mb-1">
                                    Eligible family members
                                </div>
                                {eligibleAdults.map((adult) => {
                                    const adultChecked = includedSubIds.has(adult.id);
                                    const dependants = dependantsByProvider.get(adult.id) || [];
                                    return (
                                        <div key={adult.id}>
                                            <label
                                                htmlFor={`calc-sub-${adult.id}`}
                                                className="flex items-center gap-2 text-sm text-navy-700 cursor-pointer"
                                            >
                                                <input
                                                    id={`calc-sub-${adult.id}`}
                                                    type="checkbox"
                                                    data-testid={`calc-sub-check-${adult.id}`}
                                                    checked={adultChecked}
                                                    onChange={() => toggleSub(adult)}
                                                    className="accent-navy-700 h-4 w-4"
                                                />
                                                <span>
                                                    {adult.name}{" "}
                                                    <span className="text-xs text-navy-400">
                                                        · {adult.relationship}
                                                    </span>
                                                </span>
                                            </label>
                                            {dependants.map((dep) => {
                                                const depChecked = includedSubIds.has(dep.id);
                                                return (
                                                    <label
                                                        key={dep.id}
                                                        htmlFor={`calc-sub-${dep.id}`}
                                                        className={`ml-6 flex items-center gap-2 text-xs mt-1.5 ${
                                                            adultChecked
                                                                ? "text-navy-600 cursor-pointer"
                                                                : "text-navy-300 cursor-not-allowed"
                                                        }`}
                                                    >
                                                        <input
                                                            id={`calc-sub-${dep.id}`}
                                                            type="checkbox"
                                                            data-testid={`calc-sub-check-${dep.id}`}
                                                            checked={depChecked}
                                                            disabled={!adultChecked}
                                                            onChange={() => toggleSub(dep)}
                                                            className="accent-navy-700 h-3.5 w-3.5"
                                                        />
                                                        <span>
                                                            {dep.name}{" "}
                                                            <span className="text-navy-300">
                                                                · {dep.relationship}
                                                            </span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </aside>

                    {/* Right — active member form */}
                    <div className="space-y-6">
                        {/* Identity comes from the account record, not this page — a
                            quote whose DOB disagrees with the policy record is worthless.
                            Missing fields are named here with a link to fix them. */}
                        {activeQuote?.missing_basic?.length > 0 && (
                            <div
                                className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3 text-sm"
                                data-testid="calc-missing-basic"
                            >
                                <AlertTriangle
                                    className="text-amber-500 shrink-0 mt-0.5"
                                    size={16}
                                />
                                <div className="text-navy-700">
                                    <span className="font-semibold">
                                        {active.name || "This member"}
                                    </span>{" "}
                                    can&rsquo;t be quoted until{" "}
                                    {activeQuote.missing_basic.join(", ")}{" "}
                                    {activeQuote.missing_basic.length > 1 ? "are" : "is"} on
                                    file.{" "}
                                    <Link
                                        to={active.isPrimary ? "/member/profile" : "/member/family"}
                                        className="text-gold-600 underline underline-offset-2"
                                    >
                                        {active.isPrimary
                                            ? "Update your profile"
                                            : "Update family details"}
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Basic information */}
                        <Card>
                            <SectionTitle>Basic information</SectionTitle>
                            <div className="grid sm:grid-cols-3 gap-4">
                                <FormField label="Age">
                                    <div
                                        className="h-10 flex items-center text-sm text-navy-700"
                                        data-testid="calc-age"
                                    >
                                        {activeQuote?.age ?? computeAge(active.dob) ?? "—"}
                                    </div>
                                </FormField>
                                <FormField label="Gender">
                                    <div
                                        className="h-10 flex items-center text-sm text-navy-700 capitalize"
                                        data-testid="calc-gender"
                                    >
                                        {active.gender || "—"}
                                    </div>
                                </FormField>
                                <FormField label="Date of Birth">
                                    <div
                                        className="h-10 flex items-center text-sm text-navy-700"
                                        data-testid="calc-dob"
                                    >
                                        {active.dob || "—"}
                                    </div>
                                </FormField>
                                <FormField label="Area" required>
                                    <Select
                                        value={active.area}
                                        onValueChange={(v) => setActive({ area: v })}
                                    >
                                        <SelectTrigger data-testid="calc-area">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.areas.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Network" required>
                                    <Select
                                        value={active.network}
                                        onValueChange={(v) => setActive({ network: v })}
                                    >
                                        <SelectTrigger data-testid="calc-network">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.networks.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Residence" required>
                                    <Select
                                        value={active.residence}
                                        onValueChange={(v) => setActive({ residence: v })}
                                    >
                                        <SelectTrigger data-testid="calc-residence">
                                            <SelectValue placeholder="Select residence" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.residences.map((z) => (
                                                <SelectItem key={z.residence} value={z.residence}>
                                                    {z.residence}
                                                    {z.discount_pct > 0 && (
                                                        <span className="text-navy-400">
                                                            {" "}
                                                            · −{z.discount_pct}%
                                                        </span>
                                                    )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                            </div>
                        </Card>

                        {/* Coverage */}
                        <SectionTitle>Coverage</SectionTitle>
                        <div className="grid md:grid-cols-2 gap-4">
                            {options.section_order.map((key) => {
                                const cfg = options.sections[key];
                                // Maternity is female-only — the server enforces
                                // this independently, regardless of what is sent.
                                if (
                                    cfg.gender &&
                                    String(active.gender || "").toLowerCase() !== cfg.gender
                                ) {
                                    return null;
                                }
                                const state = active.sections[key] || {};
                                const sq = activeQuote?.sections?.[key];
                                const testid = SECTION_UI[key].testid;
                                const alwaysOn = Boolean(cfg.always_on);
                                return (
                                    <CoverageCard
                                        key={key}
                                        testid={testid}
                                        title={cfg.label}
                                        checked={alwaysOn ? true : Boolean(state.enabled)}
                                        locked={alwaysOn}
                                        onCheckedChange={(v) =>
                                            setSection(key, { enabled: v })
                                        }
                                    >
                                        <div className="grid grid-cols-2 gap-3">
                                            <FormField label="Plan" required small>
                                                <Select
                                                    value={state.plan || ""}
                                                    onValueChange={(v) => {
                                                        // Cap options are plan-dependent, so a
                                                        // plan change can orphan the chosen cap.
                                                        const patch = { plan: v };
                                                        if (cfg.caps_by_plan) {
                                                            const caps = cfg.caps_by_plan[v] || [];
                                                            if (!caps.includes(state.cap)) {
                                                                patch.cap = caps[0];
                                                            }
                                                        }
                                                        setSection(key, patch);
                                                    }}
                                                >
                                                    <SelectTrigger data-testid={`${testid}-plan`}>
                                                        <SelectValue placeholder="Plan *" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {cfg.plans.map((o) => (
                                                            <SelectItem key={o} value={o}>
                                                                {o}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormField>

                                            {cfg.deductibles && (
                                                <FormField label="Deductible" required small>
                                                    <Select
                                                        value={state.deductible || ""}
                                                        onValueChange={(v) =>
                                                            setSection(key, { deductible: v })
                                                        }
                                                    >
                                                        <SelectTrigger
                                                            data-testid={`${testid}-deductible`}
                                                        >
                                                            <SelectValue placeholder="Deductible *" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {cfg.deductibles.map((o) => (
                                                                <SelectItem key={o} value={o}>
                                                                    {o}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </FormField>
                                            )}

                                            {cfg.caps_by_plan && (
                                                <FormField label="Cap" required small>
                                                    <Select
                                                        value={state.cap || ""}
                                                        onValueChange={(v) =>
                                                            setSection(key, { cap: v })
                                                        }
                                                    >
                                                        <SelectTrigger data-testid={`${testid}-cap`}>
                                                            <SelectValue placeholder="Cap *" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {(
                                                                cfg.caps_by_plan[state.plan] || []
                                                            ).map((o) => (
                                                                <SelectItem key={o} value={o}>
                                                                    {o}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </FormField>
                                            )}
                                        </div>
                                        <PremiumPill
                                            status={sq?.status}
                                            value={sq?.premium}
                                            symbol={symbol}
                                            testid={`${testid}-premium`}
                                        />
                                    </CoverageCard>
                                );
                            })}
                        </div>

                        <div className="rounded-xl bg-navy-700/5 border border-navy-100 p-4 text-[11px] text-navy-500 flex gap-2 items-start">
                            <Sparkles className="text-gold-600 shrink-0 mt-0.5" size={14} />
                            <span>
                                Premiums come from the current rate table and refresh as you
                                change coverage. When you tap{" "}
                                <span className="text-navy-700 font-semibold">APPLY</span>, an
                                application is raised for everyone marked ready, and the
                                figures are recalculated at submission.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ------------------------------ atoms ------------------------------ */

function SummaryRow({ label, value, tone = "text-navy-700", negative, symbol = "$" }) {
    const shown = Math.abs(Number(value || 0)).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return (
        <div className="flex items-center justify-between border-b last:border-b-0 border-navy-50 py-2.5">
            <div className="text-xs text-navy-500 uppercase tracking-widest">
                {label}
            </div>
            <div className={`font-display text-lg ${tone}`}>
                {negative && Number(value || 0) !== 0 ? "-" : ""}{symbol}{shown}
            </div>
        </div>
    );
}

function Card({ children }) {
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60">
            {children}
        </div>
    );
}
function SectionTitle({ children }) {
    return (
        <h2 className="font-display text-xl text-navy-700 mb-4">{children}</h2>
    );
}
function FormField({ label, required, small, children }) {
    return (
        <div>
            <Label
                className={`text-navy-700 uppercase tracking-widest ${small ? "text-[10px]" : "text-xs"}`}
            >
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
function CoverageCard({
    title,
    checked,
    onCheckedChange,
    children,
    testid,
    locked,
    className = "",
}) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl bg-white shadow-soft border p-5 space-y-4 ${
                checked ? "border-gold-400" : "border-navy-100"
            } ${className}`}
        >
            <label
                className={`flex items-center gap-3 ${locked ? "" : "cursor-pointer"}`}
            >
                <Checkbox
                    checked={checked}
                    disabled={locked}
                    onCheckedChange={(v) => onCheckedChange(Boolean(v))}
                    data-testid={`${testid}-check`}
                    className="data-[state=checked]:bg-navy-700 data-[state=checked]:border-navy-700"
                />
                <span className="font-display text-lg text-navy-700">{title}</span>
                {locked && (
                    <span className="text-[10px] uppercase tracking-widest text-navy-400">
                        Always included
                    </span>
                )}
            </label>
            <div className={checked ? "" : "opacity-50 pointer-events-none"}>
                {children}
            </div>
        </div>
    );
}

/**
 * A section's price. There is deliberately no fallback number: when the rate
 * table has no row for the chosen combination the member is told so, rather
 * than being shown an invented figure they could then submit.
 */
function PremiumPill({ status, value, symbol = "$", testid }) {
    const unpriced = status === "unpriced";
    const priced = status === "priced";
    return (
        <div
            className={`rounded-lg p-3 mt-3 border ${
                unpriced ? "bg-amber-50 border-amber-200" : "bg-cream border-gold-200/60"
            }`}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-widest text-navy-500">
                {unpriced ? "Not available" : "Premium"}
            </div>
            <div
                className={`font-display mt-0.5 ${
                    unpriced ? "text-sm text-amber-700 pt-1" : "text-xl text-navy-700"
                }`}
            >
                {priced ? (
                    <>
                        {symbol}
                        {money(value)}
                    </>
                ) : unpriced ? (
                    "No published rate for this combination"
                ) : (
                    "—"
                )}
            </div>
        </div>
    );
}
